import { useCallback, useEffect, useRef, useState } from "react";

import { getLoadErrorMessage, isAPIError } from "@/core/services";
import { listAlocacoesPorLocal, listAlocacoesPorObra } from "./carga-de-trabalho.gateway";
import type {
  AlocacoesPorLocal,
  AlocacoesPorObra,
  AlocacoesQueryState,
  ListAlocacoesParams,
} from "./carga-de-trabalho.types";

/** Espera entre a digitação e a consulta. */
export const ALOCACOES_SEARCH_DEBOUNCE_MS = 300;
export const ALOCACOES_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar a carga de trabalho.";
export const ALOCACOES_ERROR_MESSAGE = "Não foi possível carregar a carga de trabalho.";

export interface UseAlocacoesParams {
  dataInicio: string;
  dataFim: string;
  busca: string;
  /** Desativa a consulta (ex.: a outra visão está ativa). */
  enabled?: boolean;
}

type Result<T> = AlocacoesQueryState<T> & { reload: () => void };

function useAlocacoes<T>(
  load: (request: ListAlocacoesParams) => Promise<T>,
  { dataInicio, dataFim, busca, enabled = true }: UseAlocacoesParams,
): Result<T> {
  const [state, setState] = useState<AlocacoesQueryState<T>>({
    status: "loading",
    data: null,
    errorMessage: null,
  });
  const [reloadToken, setReloadToken] = useState(0);
  const previousTerm = useRef<string | null>(null);
  const term = busca.trim();

  useEffect(() => {
    if (!enabled) return undefined;

    const shouldDebounce = previousTerm.current !== null && previousTerm.current !== term;
    previousTerm.current = term;

    let cancelled = false;
    const controller = new AbortController();
    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));

    const timer = setTimeout(
      () => {
        load({ dataInicio, dataFim, busca: term, signal: controller.signal })
          .then((data) => {
            if (!cancelled) setState({ status: "success", data, errorMessage: null });
          })
          .catch((error: unknown) => {
            if (cancelled) return;
            const isForbidden = isAPIError(error) && error.status === 403;
            setState({
              status: isForbidden ? "forbidden" : "error",
              data: null,
              errorMessage: isForbidden
                ? ALOCACOES_FORBIDDEN_MESSAGE
                : getLoadErrorMessage(error, ALOCACOES_ERROR_MESSAGE),
            });
          });
      },
      shouldDebounce ? ALOCACOES_SEARCH_DEBOUNCE_MS : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [load, dataInicio, dataFim, term, enabled, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, reload };
}

export function useAlocacoesPorLocal(params: UseAlocacoesParams): Result<AlocacoesPorLocal> {
  return useAlocacoes(listAlocacoesPorLocal, params);
}

export function useAlocacoesPorObra(params: UseAlocacoesParams): Result<AlocacoesPorObra> {
  return useAlocacoes(listAlocacoesPorObra, params);
}
